import React from 'react';
import {Modal, ModalActions, ModalBody} from '../../../../components/Modal';
import {Button} from '../../../../components/Button';
import Typography from '../../../../components/Typography';
import EmployeeAvatar from '../../../../components/EmployeeAvatar';
import {AbstractRequest} from '../../../../api/RequestApi';
import {useTranslation} from 'react-i18next';
import {useAuth} from '../../../../security/AuthProvider';
import {
    REQUEST_APPROVE_ANY_PERMISSION,
    REQUEST_LEAVES_APPROVE_ANY_PERMISSION,
    REQUEST_LEAVES_REJECT_ANY_PERMISSION,
} from '../../../../security/Permissions';

// ── Badges ────────────────────────────────────────────────────────────────────

const TYPE_BADGE_STYLES: Record<string, string> = {
    LEAVE: 'bg-blue-50 text-blue-700 ring-blue-600/20',
    TIME_RECORD: 'bg-amber-50 text-amber-700 ring-amber-600/20',
    COMMAND: 'bg-gray-50 text-gray-600 ring-gray-500/20',
};

const STATE_BADGE_STYLES: Record<string, string> = {
    PENDING: 'bg-yellow-50 text-yellow-700 ring-yellow-600/20',
    APPROVED: 'bg-green-50 text-green-700 ring-green-600/20',
    REJECTED: 'bg-red-50 text-red-700 ring-red-600/20',
    CANCELLED: 'bg-gray-50 text-gray-500 ring-gray-400/20',
};

export function TypeBadge({type}: { type: string }) {

    const {t} = useTranslation();

    return (
        <span
            className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${TYPE_BADGE_STYLES[type] ?? TYPE_BADGE_STYLES.COMMAND}`}>
            {t(`request.requestType.${type.toLowerCase()}`, {defaultValue: type})}
        </span>
    );
}

export function StateBadge({state}: { state: string }) {
    const {t} = useTranslation();
    return (
        <span
            className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${STATE_BADGE_STYLES[state] ?? STATE_BADGE_STYLES.PENDING}`}>
            {t(`request.requestState.${state.toLowerCase()}`, {defaultValue: state})}
        </span>
    );
}

// ── Detail row helpers ────────────────────────────────────────────────────────

export function DetailRow({label, children}: { label: string; children: React.ReactNode }) {
    return (
        <div className="flex justify-between items-start py-2 border-b border-gray-100 last:border-b-0">
            <span className="text-sm text-gray-500 shrink-0 mr-4">{label}</span>
            <span className="text-sm text-gray-900 text-right">{children}</span>
        </div>
    );
}

export function DetailSection({title, children}: { title?: string; children: React.ReactNode }) {
    return (
        <div className="mb-4">
            {title && (
                <Typography variant="subtitle2" className="mb-2 text-gray-700">
                    {title}
                </Typography>
            )}

            <div className="rounded-md border border-gray-200 px-3 divide-y divide-gray-100">
                {children}
            </div>
        </div>
    );
}

// ── Base modal ────────────────────────────────────────────────────────────────

export interface BaseRequestDetailModalProps {
    request: AbstractRequest;
    open: boolean;
    onClose: () => void;
    onApprove?: (request: AbstractRequest) => Promise<void>;
    onReject?: (request: AbstractRequest) => Promise<void>;
    approving?: boolean;
    rejecting?: boolean;
    /** Type-specific content rendered inside the modal body below the common section. */
    children?: React.ReactNode;
    title: string;
}

export function BaseRequestDetailModal({
                                           request,
                                           open,
                                           onClose,
                                           onApprove,
                                           onReject,
                                           approving = false,
                                           rejecting = false,
                                           children,
                                           title,
                                       }: BaseRequestDetailModalProps) {

    const {t, i18n} = useTranslation();
    const {hasAnyAuthority} = useAuth();
    const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';

    const canApprove = hasAnyAuthority([REQUEST_APPROVE_ANY_PERMISSION, REQUEST_LEAVES_APPROVE_ANY_PERMISSION]);
    const canReject = hasAnyAuthority([REQUEST_APPROVE_ANY_PERMISSION, REQUEST_LEAVES_REJECT_ANY_PERMISSION]);
    const loading = approving || rejecting;

    const submittedAt = request.createdAt
        ? new Date(request.createdAt).toLocaleDateString(locale, {
            day: 'numeric', month: 'long', year: 'numeric',
        })
        : '–';

    const handleApprove = async () => {
        await onApprove?.(request);
        onClose();
    };

    const handleReject = async () => {
        await onReject?.(request);
        onClose();
    };

    return (
        <Modal isOpen={open} onClose={onClose} size="lg" title={title}>
            <ModalBody>
                {/* Employee header */}
                {request.employee && (
                    <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
                        <EmployeeAvatar employee={request.employee}/>

                        <div>
                            <Typography variant="subtitle1" className="font-medium">
                                {request.employee.preferredName}
                            </Typography>
                            {/*{request.employee && (*/}
                            {/*    <Typography variant="body2" color="secondary">*/}
                            {/*        {request.employee.jobTitle}*/}
                            {/*    </Typography>*/}
                            {/*)}*/}
                        </div>

                        <div className="ml-auto flex items-center gap-2">
                            <TypeBadge type={request.type}/>
                            <StateBadge state={request.state}/>
                        </div>
                    </div>
                )}

                {/* Common metadata */}
                <DetailSection title={t('request.detail.metadata')}>
                    <DetailRow label={t('request.detail.submittedAt')}>
                        {submittedAt}
                    </DetailRow>

                    {request.description && (
                        <DetailRow label={t('request.detail.description')}>
                            {request.description}
                        </DetailRow>
                    )}
                </DetailSection>

                {/* Type-specific content */}
                {children}
            </ModalBody>

            <ModalActions>
                <Button variant="outlined" onPress={onClose} isDisabled={loading}>
                    {t('common.close')}
                </Button>

                {request.state === 'PENDING' && onReject && canReject && (
                    <Button
                        variant="outlined"
                        loading={rejecting}
                        isDisabled={loading}
                        onPress={handleReject}
                    >
                        {t('request.action.reject')}
                    </Button>
                )}

                {request.state === 'PENDING' && onApprove && canApprove && (
                    <Button
                        variant="contained"
                        loading={approving}
                        isDisabled={loading}
                        onPress={handleApprove}
                    >
                        {t('request.action.approve')}
                    </Button>
                )}
            </ModalActions>
        </Modal>
    );
}